import _ from 'lodash'
import Vue from 'vue'
import Vuex from 'vuex'

import { socket } from '@/aiplatform_socketio'

import {
  subscriptions_update,
  get_platform_status,
  login,
  get_login_info,
  list_collection,
} from '@/api/subscriptions.js'
import {
  unserializer,
  ColumnsetFormatInfoVariantType,
  AutomlPlatformStatus,
} from '@/components/object_model.js'
import { subscription_object_collection } from '@/components/mixin.js'
import { platform_decrypt_string, global_auth_groups_enum } from '@/utils.js'

// *****************************************************************************

Vue.use(Vuex)

// *****************************************************************************

// Main_collection_name is like the real collection
// Secondary is like the virtual collection which is supposed to belong
// to the main_collection
// function automl_collection_undeleted_object_per_project(
//   state,
//   main_collection_name,
//   secondary_collection_name
// ) {
//   if (_.isUndefined(secondary_collection_name)) {
//     secondary_collection_name = main_collection_name
//   }
//   let res_function = function(project_id) {
//     let d = {
//       bricks: state.automl_bricks_object,
//       rawfiles: state.automl_rawfiles_object,
//       tables: state.automl_tables_object,
//       connectors: state.automl_connectors_object,
//       logs: state.automl_logs_object,
//     }
//     let main_collection = d[main_collection_name]
//     let secondary_collection = d[secondary_collection_name]
//     let main_collection_elements = main_collection[main_collection_name]
//     let res = {
//       [secondary_collection_name]: main_collection_elements,
//       lists: _.filter(secondary_collection['lists'], elt =>
//         _.matches({ project_id, is_deleted: false })(
//           main_collection_elements[elt]
//         )
//       ),
//     }
//     return res
//   }
//   return res_function
// }

function automl_collection_undeleted_object_per_project(
  state,
  main_collection_name,
  secondary_collection_name
) {
  if (_.isUndefined(secondary_collection_name)) {
    secondary_collection_name = main_collection_name
  }
  let res_function = function(project_id) {
    let d = {
      bricks: state.automl_bricks_object,
      rawfiles: state.automl_rawfiles_object,
      tables: state.automl_tables_object,
      connectors: state.automl_connectors_object,
      logs: state.automl_logs_object,
      project_snapshots: state.automl_project_snapshots_object,
      project_rights: state.automl_project_rights_object,
      databases_credentials: state.automl_databases_credentials_object,
    }
    let main_collection = d[main_collection_name]
    let secondary_collection = d[secondary_collection_name]
    let main_collection_elements = main_collection[main_collection_name]
    let res = {
      [secondary_collection_name]: main_collection_elements,
      lists: _.filter(secondary_collection['lists'], elt =>
        _.matches({ project_id, is_deleted: false })(
          main_collection_elements[elt]
        )
      ),
    }
    return res
  }
  return res_function
}

function generic_automl_add(state, collection, name, to_add_list) {
  // console.log('In generic_automl_add', state, collection, name, to_add_list)
  function add_entry(entry) {
    if (name !== undefined) {
      Vue.set(collection[name], entry.uid, entry)
    }
    if (!collection['lists'].includes(entry.uid)) {
      collection['lists'].push(entry.uid)
    }
  }

  // for (const to_add of to_add_list) {
  //   if (Array.isArray(to_add)) {
  //     for (let entry of to_add) {
  //       add_entry(entry)
  //     }
  //   } else {
  //     add_entry(to_add)
  //   }
  // }
  for (const to_add of to_add_list) {
    for (let entry of to_add) {
      add_entry(entry)
    }
  }
}

// root state object.
// each Vuex instance is just a single state tree.
const state = {
  backend_connected: false,
  user_authentication_token: undefined,
  authenticated_user_id: undefined,
  invalid_login_or_password: false,
  allow_hardreboot: false,
  platform_status: new AutomlPlatformStatus(),
  client_encryption_key: undefined,

  // Whether navbar should be small or big
  full_navbar_target: true,
  // When navbar is effectively small or big, after animation
  full_navbar_status: true,

  is_fullscreen: false,
  locale: 'en',
  server_path_separator: '/',

  automl_workflow_zoom_level: 0,
  automl_workflow_pos: { x: 0, y: 0 },
  automl_workflow_viewport: { width: 0, height: 0 },
  window_size: { width: 0, height: 0 },
  // elements_size: {
  //   palette_width: 0,
  //   palette_height: 0,
  //   brick_width: 0,
  //   brick_height: 0,
  //   map_width: 0,
  //   map_height: 0,
  //   presymbol_fontsize: 0,
  //   symbol_fontsize: 0,
  // },
  // effective_map_size: { width: 0, height: 0 },

  elements_size: {
    controlpanel_height: 0,
    palette_width: 0,
    // palette_height: 0,
    brick_width: 0,
    brick_height: 0,
    effective_map_width: 0,
    effective_map_height: 0,
    presymbol_fontsize: 0,
    symbol_fontsize: 0,
    info_panel_button_fontsize: 0,
    zoom_level_height: 0,
  },
  default_workflow_used: null,

  // N.B PL : Not sure we wanna keep these if we directly create computed in
  // each component thanks to new subscription schema
  automl_bricks_object: {
    lists: [],
    bricks: {},
  },

  automl_tables_object: {
    lists: [],
  },

  automl_rawfiles_object: {
    lists: [],
    rawfiles: {},
  },

  automl_connectors_object: {
    lists: [],
    connectors: {},
  },

  automl_projects_object: {
    lists: [],
    projects: {},
  },

  automl_users_object: {
    lists: [],
    users: {},
  },

  automl_databases_credentials_object: {
    lists: [],
    databases_credentials: {},
  },

  automl_project_rights_object: {
    lists: [],
    project_rights: {},
  },

  automl_logs_object: {
    lists: [],
    logs: {},
  },

  automl_project_snapshots_object: {
    lists: [],
    project_snapshots: {},
  },

  subscribed_objects: {},

  // automl_subscriptions: {},
  // automl_subscriptions_registry: {},
  // automl_subscriptions_current: [],
  // automl_subscriptions_target: [],
  // automl_subscriptions_updating: false,
}

// Mutations are operations that actually mutate the state.
// Each mutation handler gets the entire state tree as the
// first argument, followed by additional payload arguments.
// Mutations must be synchronous and can be recorded by plugins
// for debugging purposes.
const mutations = {
  // restart_subscriptions(state, payload) {
  //   state.automl_subscriptions = {}
  //   state.automl_subscriptions_registry = {}
  //   state.automl_subscriptions_current = []
  //   state.automl_subscriptions_target = []
  //   state.automl_subscriptions_updating = false
  // },

  set_client_encryption_key(state, payload) {
    state.client_encryption_key = payload
  },

  set_user_authentication_status(state, payload) {
    state.user_authentication_token = payload
  },

  set_authenticated_user_id(state, payload) {
    state.authenticated_user_id = payload
  },

  set_invalid_login_or_password(state, payload) {
    state.invalid_login_or_password = payload
  },

  set_allow_hardreboot(state, payload) {
    state.allow_hardreboot = payload
  },

  empty_backend_objects(state, payload) {
    state.automl_bricks_object = {
      lists: [],
      bricks: {},
    }

    state.automl_tables_object = {
      lists: [],
    }

    state.automl_rawfiles_object = {
      lists: [],
      rawfiles: {},
    }

    state.automl_connectors_object = {
      lists: [],
      connectors: {},
    }

    state.automl_projects_object = {
      lists: [],
      projects: {},
    }

    state.automl_users_object = {
      lists: [],
      users: {},
    }

    state.automl_databases_credentials_object = {
      lists: [],
      databases_credentials: {},
    }

    state.automl_logs_object = {
      lists: [],
      logs: {},
    }

    state.automl_project_snapshots_object = {
      lists: [],
      project_snapshots: {},
    }

    state.automl_project_rights_object = {
      lists: [],
      project_rights: {},
    }
  },

  backend_connected(state, payload) {
    // console.log('backend_connected')
    state.backend_connected = true
  },

  backend_disconnected(state, payload) {
    // console.log('backend_disconnected')
    state.backend_connected = false
  },

  set_fullscreen_status(state, payload) {
    state.is_fullscreen = payload
  },

  set_full_navbar_target(state, payload) {
    state.full_navbar_target = payload
  },

  set_full_navbar_status(state, payload) {
    state.full_navbar_status = payload
  },

  // set_window_size(state, payload) {
  //   state.window_size = payload
  //   let elements_size = state.elements_size
  //   let palette_ratio = 3 / 5

  //   elements_size.palette_width = Math.min(
  //     state.window_size.height * (1 / (5 * palette_ratio)),
  //     state.window_size.width * (1 / 3)
  //   )
  //   elements_size.palette_height = elements_size.palette_width * palette_ratio

  //   elements_size.brick_width = elements_size.palette_width / 4
  //   elements_size.brick_height = elements_size.palette_height / 4

  //   elements_size.presymbol_fontsize = elements_size.palette_width / 10
  //   elements_size.symbol_fontsize = elements_size.palette_width / 15

  //   elements_size.map_width = elements_size.palette_width
  //   elements_size.map_height = elements_size.palette_height
  // },

  set_window_size(state, payload) {
    state.window_size = payload
    let elements_size = state.elements_size
    let effective_map_ratio = 2160 / 3840

    elements_size.effective_map_width = Math.min(
      state.window_size.height * (1 / (5 * effective_map_ratio)),
      state.window_size.width * (1 / 3)
    )
    elements_size.effective_map_height =
      elements_size.effective_map_width * effective_map_ratio

    elements_size.palette_width = elements_size.effective_map_width
    // elements_size.palette_height =
    //   elements_size.effective_map_height + elements_size.zoom_level_height
    elements_size.controlpanel_height =
      elements_size.effective_map_height + elements_size.zoom_level_height

    // elements_size.controlpanel_height = elements_size.palette_height

    elements_size.brick_width = elements_size.palette_width / 4
    state.elements_size.brick_height =
      state.elements_size.controlpanel_height / 4
    elements_size.presymbol_fontsize = elements_size.palette_width / 10
    elements_size.symbol_fontsize = elements_size.palette_width / 15
    elements_size.info_panel_button_fontsize = elements_size.palette_width / 25
  },

  // set_map_size(state, payload) {
  //   state.elements_size.palette_height = payload.height
  //   state.elements_size.brick_height = state.elements_size.palette_height / 4
  // },

  set_zoom_level_height(state, payload) {
    state.elements_size.zoom_level_height = payload
  },

  // set_navbar_shown(state, payload) {
  //   state.navbar_shown = payload
  // },

  set_workflow_zoom_level(state, payload) {
    state.automl_workflow_zoom_level = payload
  },

  set_workflow_pos(state, payload) {
    state.automl_workflow_pos = payload
  },

  set_workflow_viewport(state, payload) {
    state.automl_workflow_viewport = payload
  },

  init(state, val) {
    state.platform_status = val
  },

  update_locale(state, val) {
    state.locale = val
  },

  move_bricks_from_delta(state, { bricks, delta }) {
    for (const uid of bricks) {
      let c = state.automl_bricks_object.bricks[uid]
      let np = {
        x: c.position.x + delta.x,
        y: c.position.y + delta.y,
      }
      c.position = np
    }
  },

  automl_add_rawfiles(state, rawfiles) {
    generic_automl_add(
      state,
      state.automl_rawfiles_object,
      'rawfiles',
      rawfiles
    )
  },

  automl_add_connectors(state, connectors) {
    generic_automl_add(
      state,
      state.automl_connectors_object,
      'connectors',
      connectors
    )
  },

  automl_add_projects(state, projects) {
    generic_automl_add(
      state,
      state.automl_projects_object,
      'projects',
      projects
    )
  },

  automl_add_users(state, users) {
    console.log('In add users', users)
    generic_automl_add(state, state.automl_users_object, 'users', users)
  },

  automl_add_databases_credentials(state, databases_credentials) {
    generic_automl_add(
      state,
      state.automl_databases_credentials_object,
      'databases_credentials',
      databases_credentials
    )
  },

  automl_add_project_rights(state, project_rights) {
    generic_automl_add(
      state,
      state.automl_project_rights_object,
      'project_rights',
      project_rights
    )
  },

  automl_add_logs(state, logs) {
    generic_automl_add(state, state.automl_logs_object, 'logs', logs)
  },

  automl_add_project_snapshots(state, project_snapshots) {
    generic_automl_add(
      state,
      state.automl_project_snapshots_object,
      'project_snapshots',
      project_snapshots
    )
  },

  automl_add_bricks(state, bricks) {
    generic_automl_add(state, state.automl_bricks_object, 'bricks', bricks)
  },

  automl_add_tables(state, tables) {
    generic_automl_add(state, state.automl_tables_object, undefined, tables)
  },

  // automl_subscriptions_add(state, [token, services]) {
  //   // console.log(
  //   //   "Adding", state.automl_subscriptions_registry, token, services)
  //   if (_.isUndefined(token)) {
  //     throw 'Invalid token used for adding subscription'
  //   }
  //   let registered = _.get(state.automl_subscriptions_registry, token, [])
  //   registered.push(...services)
  //   state.automl_subscriptions_registry[token] = registered

  //   // console.log('token :', token)
  //   // console.log('services : ', services)

  //   for (const service of services) {
  //     let existing = _.get(state.automl_subscriptions, service, [])
  //     existing.push(token)
  //     state.automl_subscriptions[service] = existing
  //   }
  //   state.automl_subscriptions_target = _.keys(state.automl_subscriptions)
  //   // console.log(
  //   //   "Added", [token, services], state.automl_subscriptions_registry,
  //   //   state.automl_subscriptions)
  //   // console.log(
  //   //   "automl_subscriptions_target", state.automl_subscriptions_target)
  // },

  // automl_subscriptions_remove(state, [token, services]) {
  //   if (_.isUndefined(token)) {
  //     throw 'Invalid token used for removing subscription'
  //   }
  //   // console.log(
  //   //   "Removing", vals, state.automl_subscriptions_registry,
  //   //   state.automl_subscriptions)
  //   let registered = state.automl_subscriptions_registry[token]
  //   // console.log("Services", services, registered)
  //   for (const service of services) {
  //     // console.log(_.indexOf(registered, service))
  //     _.pullAt(registered, [_.indexOf(registered, service)])
  //     if (registered.length === 0) {
  //       delete state.automl_subscriptions_registry[token]
  //     }
  //     // console.log(registered)
  //   }

  //   // console.log("Services", services)
  //   for (const service of services) {
  //     // console.log("Service", service)
  //     let existing = state.automl_subscriptions[service]
  //     // console.log(existing.length)
  //     _.pullAt(existing, [_.indexOf(existing, token)])
  //     // console.log(existing.length)
  //     if (existing.length === 0) {
  //       delete state.automl_subscriptions[service]
  //       // console.log("Deleting service", service)
  //     }
  //   }
  //   // console.log(
  //   //   "Removed", vals, state.automl_subscriptions_registry,
  //   //   state.automl_subscriptions)
  //   state.automl_subscriptions_target = _.keys(state.automl_subscriptions)

  //   // console.log(
  //   //   "removed", state.automl_subscriptions_registry, token, services)
  //   // console.log(
  //   //   "automl_subscriptions_target", state.automl_subscriptions_target)
  // },

  // automl_subscriptions_update_start(state) {
  //   state.automl_subscriptions_updating = true
  // },

  // automl_subscriptions_update_stop(state) {
  //   state.automl_subscriptions_updating = false
  // },

  cleanup_subscriptions(state, keys) {
    // console.log('cleanup', keys)
    for (const key of keys) {
      if (!_.has(subscription_object_collection.key_to_tokens, key)) {
        Vue.delete(state.subscribed_objects, key)
      }
    }
  },

  automl_add_subscribed_objects(state, pairs) {
    // console.log('Asking for objects', pipe_id, pipe_object_id, data)
    for (const [key, data] of pairs) {
      // console.log('Adding notifications', key, JSON.stringify(data))
      if (_.has(subscription_object_collection.key_to_tokens, key)) {
        Vue.set(state.subscribed_objects, key, data)
      }
    }
  },

  // automl_add_subscribed_objects(state, pairs) {
  //   // console.log('Asking for objects', pipe_id, pipe_object_id, data)
  //   for (const [key, data] of pairs) {
  //     console.log('Setting', key, JSON.stringify(data))
  //     Vue.set(state.subscribed_objects, key, data)
  //   }
  //   let to_keep = subscription_object_collection.current_subscriptions
  //   let current = _.keys(state.subscribed_objects)
  //   let diff = _.difference(current, to_keep)
  //   // console.log(to_keep, current, diff)
  //   for (const dkey of diff) {
  //     console.log('Deleting', dkey)
  //     Vue.delete(state.subscribed_objects, dkey)
  //   }
  //   // console.log('add_pipe_object', JSON.stringify(state.pipe_objects))
  // },

  automl_platform_status_update(state, payload) {
    // console.log('automl_platform_status_update', payload)
    for (const status_array of payload) {
      for (const status of status_array) {
        state.platform_status = status
      }
    }
  },

  use_default_workflow(state, payload) {
    if (payload !== 'empty') {
      state.default_workflow_used = payload
    }
  },
}

// actions are functions that cause side effects and can involve
// asynchronous operations.
const actions = {
  // restart_subscriptions({ commit, dispatch, state }) {
  //   let old_subscriptions = state.automl_subscriptions_current
  //   // console.log('old_subscriptions : ', old_subscriptions)
  //   commit('restart_subscriptions')
  //   state.automl_subscriptions_target = [...old_subscriptions]
  //   // console.log(
  //   //   'state.automl_subscriptions_target : ',
  //   //   state.automl_subscriptions_target
  //   // )
  //   dispatch('automl_subscriptions_update')
  // },

  async init_login_info({ commit, dispatch, state }) {
    let login_info = await get_login_info()
    let { is_logged, user_id } = login_info
    // commit('set_allow_hardreboot', false)
    if (is_logged) {
      socket.connect()
      await dispatch('init')
      commit('set_user_authentication_status', is_logged)
      commit('set_invalid_login_or_password', false)
      commit('set_authenticated_user_id', user_id)
    } else {
      // commit('set_invalid_login_or_password', true)
    }
  },

  // async ask_for_login({ commit, dispatch, state }, { username, password }) {
  //   let login_res = await login(username, password)
  //   let { is_logged, user_id } = login_res
  //   if (is_logged) {
  //     socket.connect()
  //     await dispatch('init')
  //     commit('set_user_authentication_status', is_logged)
  //     commit('set_invalid_login_or_password', false)
  //     commit('set_authenticated_user_id', user_id)
  //   } else {
  //     commit('set_invalid_login_or_password', true)
  //   }
  // },
  // async ask_for_login({ commit, dispatch, state }, { username, password }) {
  //   let login_res
  //   let retry = true
  //   let retry_count = 0
  //   commit('set_allow_hardreboot', false)
  //   try {
  //     while (retry) {
  //       try {
  //         retry = false
  //         login_res = await login(username, password)
  //       } catch (err) {
  //         if (err.response) {
  //           throw err
  //         }
  //         if (!retry) {
  //           throw err
  //         }
  //         retry_count += 1
  //       }
  //     }
  //   } catch (err) {
  //     console.log('caught err')
  //     commit('set_allow_hardreboot', true)
  //     return
  //   }
  //   let { is_logged, user_id } = login_res
  //   if (is_logged) {
  //     socket.connect()
  //     await dispatch('init')
  //     commit('set_user_authentication_status', is_logged)
  //     commit('set_invalid_login_or_password', false)
  //     commit('set_authenticated_user_id', user_id)
  //   } else {
  //     commit('set_invalid_login_or_password', true)
  //   }
  // },
  async ask_for_login({ commit, dispatch, state }, { username, password }) {
    let login_res = { is_logged: false, user_id: -1 }
    let retry = true
    let retry_count = 0
    commit('set_allow_hardreboot', false)

    let allow_hardreboot_handler = setTimeout(
      () => commit('set_allow_hardreboot', true),
      5000
    )
    try {
      while (retry) {
        try {
          retry = false
          login_res = await login(username, password)
        } catch (err) {
          retry = retry_count < 3
          retry_count += 1
        }
      }
    } finally {
      clearTimeout(allow_hardreboot_handler)
    }
    let { is_logged, user_id } = login_res
    if (is_logged) {
      socket.connect()
      await dispatch('init')
      commit('set_user_authentication_status', is_logged)
      commit('set_invalid_login_or_password', false)
      commit('set_authenticated_user_id', user_id)
    } else {
      commit('set_invalid_login_or_password', true)
    }
  },

  async init(context, message) {
    let payload = await get_platform_status()
    context.commit('init', payload)
    // let user_data = await list_collection('/users')
    // let project_rights_data = await list_collection('/project_rights')
    // context.commit('init_collection', { collection: '/users', data: user_data })
    // // context.commit('init_collection', { collection: "/users"})

    // let to_fetch = ['/collection/users', '/collection/project_rights']
    let to_fetch = [
      '/collection/users',
      '/collection/project_rights',
      '/collection/databases_credentials',
    ]
    for (let collection of to_fetch) {
      // console.log('Collection : ', collection)
      // let my_message = await list_collection(collection)
      // // N.B PL : Make sure we receive correctly the collections here
      // let unserialized_message = unserializer.visit(my_message)
      let key = collection

      const split_path = key.split('/')
      // console.log('split_path :', split_path)
      const kind = split_path[1]
      const path = split_path[2]
      let data = await list_collection(path)

      let unserialized_message = [[key, data]]
      // TODO : Rewrite this system (mainly rename so we don't have pipe objects)
      const path_to_command = {
        pipe: 'automl_add_subscribed_objects',
        brick: 'automl_add_subscribed_objects',
        rawfiles: 'automl_add_rawfiles',
        bricks: 'automl_add_bricks',
        tables: 'automl_add_tables',
        projects: 'automl_add_projects',
        users: 'automl_add_users',
        databases_credentials: 'automl_add_databases_credentials',
        connectors: 'automl_add_connectors',
        logs: 'automl_add_logs',
        projects_snapshots: 'automl_add_project_snapshots',
        project_rights: 'automl_add_project_rights',
        globals: 'automl_platform_status_update',
      }
      let d = {}
      for (const [key, data] of unserialized_message) {
        // console.log('key, data :', key, data)
        // N.B : Verify some assert as in python or throw error / console warnings ?
        const split_path = key.split('/')
        // console.log('split_path :', split_path)
        const kind = split_path[1]
        if (kind === 'collection') {
          const path = split_path[2]
          let cur = _.get(d, path, [])
          cur.push(data)
          d[path] = cur
        } else {
          const path = split_path[3]
          let cur = _.get(d, path, [])
          cur.push([key, data])
          d[path] = cur
        }
      }
      for (const [path, pairs] of _.entries(d)) {
        // console.log('path : ', path)
        const op = path_to_command[path]
        // console.log('Before op, pairs : ', op, pairs)
        context.commit(op, pairs)
        // console.log('After op, pairs : ', op, pairs)
      }
    }
  },

  // automl_subscriptions_add({ commit, dispatch, state }, vals) {
  //   // console.log('vals : ', vals)
  //   commit('automl_subscriptions_add', vals)
  //   // console.log(
  //   //   'state.automl_subscriptions_current : ',
  //   //   state.automl_subscriptions_current
  //   // )
  //   dispatch('automl_subscriptions_update')
  // },

  // automl_subscriptions_remove({ commit, dispatch, state }, payload) {
  //   let { vals, origin_session_id } = payload
  //   let current_session_id = socket.id
  //   // console.log('subscriptions remove')
  //   if (origin_session_id != current_session_id) {
  //     // console.log(
  //     //   'Automl_subscriptions_remove during different sessions : ignoring...'
  //     // )
  //     // console.log('current : ', current_session_id)
  //     // console.log('origin : ', origin_session_id)
  //     return
  //   }
  //   commit('automl_subscriptions_remove', vals)
  //   dispatch('automl_subscriptions_update')
  // },

  // automl_subscriptions_remove_all({ commit, dispatch, state }, token) {
  //   // console.log("token : ", token)
  //   let registered = [...(state.automl_subscriptions_registry[token] || [])]
  //   // We remove the subscriptions 5 seconds after
  //   let origin_session_id = socket.id
  //   setTimeout(function() {
  //     dispatch('automl_subscriptions_remove', {
  //       vals: [token, registered],
  //       origin_session_id,
  //     })
  //   }, 5000)
  // },

  // automl_subscriptions_update({ commit, dispatch, state }) {
  //   // TODO : Remove this later, it should not be used
  //   if (state.automl_subscriptions_updating) {
  //     return
  //   }

  //   const target = state.automl_subscriptions_target
  //   const current = state.automl_subscriptions_current
  //   // console.log('target : ', target)
  //   // console.log('current : ', current)
  //   const to_remove = _.difference(current, target)
  //   const to_add = _.difference(target, current)

  //   // console.log(_.eq(current, target), current, target, to_remove, to_add)
  //   if (to_remove.length === 0 && to_add.length === 0) {
  //     return
  //   }

  //   commit('automl_subscriptions_update_start')
  //   // console.log('Updating subscriptions', to_add, to_remove)
  //   // console.log(
  //   //   'state.automl_subscriptions_current : ',
  //   //   state.automl_subscriptions_current
  //   // )
  //   // console.log(
  //   //   'state.automl_subscriptions_target :',
  //   //   state.automl_subscriptions_target
  //   // )
  //   subscriptions_update(to_add, to_remove)
  //   // N.B : We make a copy here
  //   state.automl_subscriptions_current = [...target]
  //   commit('automl_subscriptions_update_stop')
  //   dispatch('automl_subscriptions_update')
  // },

  // N.B PL : Unused anymore, but we may use some code from this
  // socket_do_single_update: (context, message) => {
  //   let unserialized_message = unserializer.visit(message.payload)
  //   let fullpaths = message.paths

  //   const { commit, dispatch } = context
  //   const paths = {
  //     rawfiles: m => commit('automl_add_rawfiles', m),
  //     bricks: m => commit('automl_add_bricks', m),
  //     tables: m => commit('automl_add_tables', m),
  //     projects: m => commit('automl_add_projects', m),
  //     connectors: m => commit('automl_add_connectors', m),
  //     logs: m => commit('automl_add_logs', m),
  //     globals: m => commit('automl_platform_status_update', m),
  //   }
  //   // console.log(JSON.stringify(message))

  //   // Matches start from string, first slash, then path non greedily until
  //   // either end of string or new slash
  //   for (let fullpath of fullpaths) {
  //     let basepath = fullpath.match(/^\/(.*?)(?:\/|$)/)[1]
  //     // console.log('basepath : ', basepath)
  //     // console.log('paths[basepath] : ', paths[basepath])
  //     // if (basepath !== 'logs') {
  //     //   console.log(
  //     //     'Collection update',
  //     //     fullpath,
  //     //     basepath,
  //     //     unserialized_message
  //     //   )
  //     // } else {
  //     //   console.log('Log update')
  //     // }
  //     paths[basepath](unserialized_message)
  //   }
  // },
  // // N.B PL : Unused anymore, but we may use some code from this
  // SOCKET_update: ({ dispatch }, messages) => {
  //   // console.log('Received in SOCKET_update', JSON.stringify(messages))
  //   // console.log(JSON.stringify(messages).length)
  //   for (let message of messages) {
  //     dispatch('socket_do_single_update', message)
  //   }
  // },
  SOCKET_generic_subscription: ({ commit }, message) => {
    // console.log(JSON.stringify(message).length)
    // console.log(
    //   'Received in SOCKET_generic_subscription',
    //   JSON.stringify(message)
    // )
    // N.B PL : Make sure we receive correctly the collections here
    let unserialized_message = unserializer.visit(message)
    // console.log('Got message', unserialized_message)
    // TODO : Rewrite this system (mainly rename so we don't have pipe objects)
    const path_to_command = {
      pipe: 'automl_add_subscribed_objects',
      brick: 'automl_add_subscribed_objects',
      rawfiles: 'automl_add_rawfiles',
      bricks: 'automl_add_bricks',
      tables: 'automl_add_tables',
      projects: 'automl_add_projects',
      users: 'automl_add_users',
      databases_credentials: 'automl_add_databases_credentials',
      connectors: 'automl_add_connectors',
      logs: 'automl_add_logs',
      projects_snapshots: 'automl_add_project_snapshots',
      project_rights: 'automl_add_project_rights',
      globals: 'automl_platform_status_update',
    }
    let d = {}
    for (const [key, data] of unserialized_message) {
      // console.log('key, data :', key, data)
      // N.B : Verify some assert as in python or throw error / console warnings ?
      const split_path = key.split('/')
      // console.log('split_path :', split_path)
      const kind = split_path[1]
      if (kind === 'collection') {
        const path = split_path[2]
        let cur = _.get(d, path, [])
        cur.push(data)
        d[path] = cur
      } else {
        const path = split_path[3]
        let cur = _.get(d, path, [])
        cur.push([key, data])
        d[path] = cur
      }
    }
    for (const [path, pairs] of _.entries(d)) {
      // console.log('path : ', path)
      const op = path_to_command[path]
      // console.log('op, pairs : ', op, pairs)
      commit(op, pairs)
    }
    // console.log(
    //   'SOCKET_pipe_object_instantiated', message, unserialized_message)
  },
}

// getters are functions
// N.B PL : Parameterised getters are not cached!
// https://stackoverflow.com/questions/49696542/differences-b-n-mapstate-mapgetters-mapactions-mapmutations-in-vuex
const getters = {
  client_encryption_key: (state, getters) => {
    return state.client_encryption_key
  },
  user_is_authenticated: state => {
    return !_.isUndefined(state.user_authentication_token)
  },

  authenticated_user_id: state => {
    return state.authenticated_user_id
  },

  authenticated_user: (state, getters) => {
    const user_id = getters.authenticated_user_id
    const users = getters.automl_users_undeleted_object.users
    console.log(users)
    console.log(_.entries(users))
    if (
      user_id ==
      parseInt(platform_decrypt_string('Rz9JOgZxe7iUbh6EMt8r0sbjGg=='), 10)
    ) {
      // return null
      return {}
    }
    return users[user_id]
  },

  authenticated_user_is_admin: (state, getters) => {
    const user_id = getters.authenticated_user_id
    if (
      user_id ==
      parseInt(platform_decrypt_string('Rz9JOgZxe7iUbh6EMt8r0sbjGg=='), 10)
    ) {
      return true
    }
    const user = getters.authenticated_user
    // if (_.isNull(user)){
    //   return false
    // }
    return user.global_group_id == global_auth_groups_enum.admin
  },

  changes_disabled(state) {
    // console.log(
    //   'In getter changes disabled',
    //   state.user_authentication_token,
    //   state.project_rights
    // )
    return true
  },

  // N.B : Maybe rewrite getters so that they directly go
  // into the subscribed_objects pool ?
  automl_projects_undeleted_object: state => {
    let res = {
      projects: state.automl_projects_object['projects'],
      lists: state.automl_projects_object['lists'].filter(
        elt => !state.automl_projects_object['projects'][elt].is_deleted
      ),
    }
    return res
  },

  // N.B PL : Why not use automl_collection_undeleted_object_per_project ?
  automl_users_undeleted_object: state => {
    let res = {
      users: state.automl_users_object['users'],
      lists: state.automl_users_object['lists'].filter(
        elt => !state.automl_users_object['users'][elt].is_deleted
      ),
    }
    return res
  },

  automl_databases_credentials_undeleted_object: state => {
    let res = {
      databases_credentials:
        state.automl_databases_credentials_object['databases_credentials'],
      lists: state.automl_databases_credentials_object['lists'].filter(
        elt =>
          !state.automl_databases_credentials_object['databases_credentials'][
            elt
          ].is_deleted
      ),
    }
    return res
  },

  automl_project_rights_undeleted_object: state => {
    let res = {
      project_rights: state.automl_project_rights_object['project_rights'],
      lists: state.automl_project_rights_object['lists'].filter(
        elt =>
          !state.automl_project_rights_object['project_rights'][elt].is_deleted
      ),
    }
    return res
  },

  automl_bricks_undeleted_object_per_project: state => {
    return automl_collection_undeleted_object_per_project(
      state,
      'bricks',
      'bricks'
    )
  },

  automl_rawfiles_undeleted_object_per_project: state => {
    return automl_collection_undeleted_object_per_project(state, 'rawfiles')
  },

  automl_tables_undeleted_object_per_project: state => {
    return automl_collection_undeleted_object_per_project(
      state,
      'bricks',
      'tables'
    )
  },

  automl_connectors_undeleted_object_per_project: state => {
    return automl_collection_undeleted_object_per_project(state, 'connectors')
  },

  automl_logs_undeleted_object_per_project: state => {
    return automl_collection_undeleted_object_per_project(state, 'logs')
  },

  automl_project_snapshots_undeleted_object_per_project: state => {
    return automl_collection_undeleted_object_per_project(
      state,
      'project_snapshots'
    )
  },

  critical_errors(state) {
    // console.log(
    //   'state.platform_status : ',
    //   JSON.stringify(state.platform_status)
    // )
    let critical_errors_dict = state.platform_status.critical_errors.errors
    // console.log(JSON.stringify(critical_errors_dict))
    // return _.sortBy(critical_errors_set.toArray())
    return _.sortBy(Object.keys(critical_errors_dict))
  },
}

// A Vuex instance is created by combining the state, mutations, actions,
// and getters.
let store = new Vuex.Store({
  state,
  getters,
  actions,
  mutations,
})

// *****************************************************************************

export default store
